import {
    AfterViewInit,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    TemplateRef
} from '@angular/core';
import {EntityCollectionCrudService} from '../../service/entityCollectionCrudService';
import {UntypedFormGroup} from '@angular/forms';
import {ContentDirective} from '../../../../shared/directives/content.directive';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {tap} from 'rxjs/operators';
import {HttpModelConverter} from '../../converter/httpModel.converter';
import {EntityCollectionDeleteService} from '../../service/entityCollectionDeleteService';
import {BaseView} from '../../view/base.view';
import {WithSummaryModel} from '../../../common/entity/dto/plain/withSummary.model';
import {AbstractSaveOneService} from '../../service/v2/service/save/abstractSaveOne.service';
import {AbstractFindOneService} from '../../service/v2/service/findone/abstractFindOneService';
import {AbstractDeleteOneService} from '../../service/v2/service/delete/abstractDeleteOne.service';


// FIXME delegate to view-by-id-default.component
@Component({
    selector: 'app-create-update-default',
    templateUrl: './create-update-default.component.html'
})
export class CreateUpdateDefaultComponent<E, P, C extends HttpModelConverter<E>> extends BaseView
    implements OnDestroy, OnChanges, AfterViewInit {

    @ContentChild(ContentDirective, {static: true, read: TemplateRef}) template: TemplateRef<any>;

    // EntityCollectionCrudService deprecated
    @Input() service: EntityCollectionCrudService<E, P, C> | AbstractSaveOneService<E, P>;

    // Mandatory if AbstractSaveOneService specified
    @Input() findOneService: AbstractFindOneService<any, E>;
    @Input() errorIfLoadEntityReturnsNull = true;


    @Input() deleteService: EntityCollectionDeleteService<E>;
    @Input() formGroup: UntypedFormGroup;
    @Input() id: number | string;
    @Input() returnUrl: string;
    @Input() returnUrlOnDeletion: string;
    @Input() inDialog = false;
    @Input() hideButtons = false;

    @Input() buildPayload: () => P;
    @Input() resolveReturnUrl: (returnUrl: string, entity: E) => string;
    @Input() hideSaveButton = false;
    @Input() hideDeleteButton = false;
    @Input() additionalLoading = false;

    @Input() hideFindOneErrorMessage = false;
    @Input() hideDeleteErrorMessage = false;
    @Input() hideSuccessMessage = false;

    @Input() dontGoBakOnSaveSuccess = false;
    @Input() saveButtonLabel = 'Enregistrer';

    @Input() successMessage = 'Modifications enregistrées';
    @Input() dontShowErrorMessage = false;


    @Output() savingErrorEvent = new EventEmitter<any>();
    @Output() saveLoadingEvent = new EventEmitter<boolean>();
    @Output() entityLoaded = new EventEmitter<E>();
    @Output() beforeSavingEvent = new EventEmitter<void>();
    @Output() entitySaved = new EventEmitter<E>();
    @Output() entityDeleted = new EventEmitter<string | number>();
    @Output() entitySaveErrorMessageEvent = new EventEmitter<string>();
    @Output() entitySaveErrorEvent = new EventEmitter<any>();
    @Output() findOneErrorEvent = new EventEmitter<any>();

    subscriptions: Subscription[] = [];
    // saveError = false;
    saveLoading = false;
    saveLoading$: Observable<boolean> = new BehaviorSubject<boolean>(false);
    findOneError: any;
    deleteByIdLoading = false;
    private queryByIdLoading = false;
    entity: E;


    constructor() {
        super();
    }

    get loading(): boolean {
        if (this.queryByIdLoading === true) {
            return true;
        } else {
            return this.additionalLoading;
        }
    }


    ngOnChanges(changes: SimpleChanges): void {
        const idChange = changes['id'];
        if (idChange) {
            // console.logByType'on id changed');
        }
        if (idChange && idChange.currentValue && this.id && !idChange.isFirstChange()) {
            // console.logByType'load entity');
            this.loadEntity();
        }
    }

    ngAfterViewInit(): void {

        if (this.deleteService) {
            if (this.deleteService instanceof AbstractDeleteOneService) {
                this.subscriptions.push(this.deleteService.loading$.subscribe(loading => this.deleteByIdLoading = loading));
            } else {
                this.subscriptions.push(this.deleteService.deleteLoading$.subscribe(loading => this.deleteByIdLoading = loading));
            }
        }

        this.subscriptions.push(this.service.error$.pipe(
            tap(error => {
                if (error === true) {
                    this.redirectToWithinCurrentApp('500');
                }
            })
        ).subscribe());

        if (this.service instanceof EntityCollectionCrudService) {
            this.subscriptions.push(this.service.saveErrors$.subscribe(value => {
                this.entitySaveErrorEvent.emit(value);
                const message = value?.payload?.data?.error?.message;
                if (this.entitySaveErrorMessageEvent) {
                    this.entitySaveErrorMessageEvent.emit(message);
                }
            }));
        } else {
            this.subscriptions.push(this.service.error$.subscribe(value => {
                const message = value?.payload?.data?.error?.message;
                this.entitySaveErrorEvent.emit(value);
                if (this.entitySaveErrorMessageEvent && message) {
                    this.entitySaveErrorMessageEvent.emit(message);
                }
            }));
        }

        if (this.id) {
            this.loadEntity();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(value => value.unsubscribe());
    }

    saveWithPayload(payload: P) {
        if (!this.formGroup.valid) {
            return;
        }
        this.beforeSavingEvent.emit();
        this.saveLoading = true;
        this.sendSaveLoading$();
        console.log(['saveWithPayload', payload, this.service]);
        const promise = this.service instanceof EntityCollectionCrudService ?
            this.service.save(payload).toPromise() :
            this.service.save(payload);
        promise.then(value => {
            if (this.entitySaved) {
                if (this.entitySaved) {
                    this.entitySaved.emit(value);
                }
                this.toastControl.presentConfirmation(this.successMessage);
                if (!this.dontGoBakOnSaveSuccess) {
                    this.gobBack(value);
                }
            }
        })
            .catch((e) => {
                this.savingErrorEvent.emit(e);
                if (!this.dontShowErrorMessage) {
                    this.toastControl.presentError(e);
                }
            })
            .finally(() => {
                this.saveLoading = false;
                this.sendSaveLoading$();
            });
    }

    sendSaveLoading$() {
        (this.saveLoading$ as BehaviorSubject<boolean>).next(this.saveLoading);
        this.saveLoadingEvent?.emit(this.saveLoading);
    }

    save() {
        if (!this.formGroup.valid) {
            return;
        }
        let payload: P;
        if (this.buildPayload) {
            payload = this.buildPayload();
        } else {
            payload = this.formGroup.value;
        }
        this.saveWithPayload(payload);
    }

    delete() {
        this.deleteService.delete(this.entity)
            .then(value => {
                this.onEntityDeleted(value);
            })
            .catch((e) => this.onEntityDeletedError(e));
    }

    public onEntityDeleted(id: number | string) {
        if (this.entityDeleted && id) {
            this.entityDeleted.emit(id);
        }
        this.toastControl.presentConfirmation('Suppression effectuée');
        const redirectTo = this.returnUrlOnDeletion ? this.returnUrlOnDeletion : this.extractReturnUrlFormUrl();
        if (redirectTo && !this.inDialog) {
            this.navController.navigateRoot(redirectTo);
        }
    }

    public onEntityDeletedError(error: any) {
        this.toastControl.presentError(error, 'Suppression impossible');
    }

    mustDisplayDeleteButton(): boolean {
        let deletable = false;
        if (this.deleteService && this.hideDeleteButton !== true && this.entity) {
            if (this.entity.hasOwnProperty('permission')) {
                deletable = (this.entity as any)?.permissions?.deletable;
            } else if (this.entity instanceof WithSummaryModel) {
                deletable = this.entity.summary.permissions.deletable;
            }
        }
        return deletable;
    }

    private gobBack(entity: E) {
        let redirectTo;

        if (!this.inDialog) {
            if (this.returnUrl) {
                redirectTo = this.resolveReturnUrl ? this.resolveReturnUrl(this.returnUrl, entity) : this.returnUrl;
            }
            if (!redirectTo) {
                redirectTo = this.extractReturnUrlFormUrl();
            }

            if (redirectTo) {
                this.redirectToWithinCurrentApp(redirectTo);
            }
        }
    }

    private loadEntity() {
        // console.log('loading');
        this.findOneError = undefined;
        this.queryByIdLoading = true;
        const promise: Promise<E> = this.service instanceof EntityCollectionCrudService ?
            this.service.loadEntity(this.id) :
            this.findOneService.loadEntity(this.id);
        promise
            .then((entity) => {
                if (!entity && this.errorIfLoadEntityReturnsNull === true) {
                    throw new Error('No entity found');
                } else {
                    this.entity = entity;
                    this.entityLoaded.emit(entity);
                }

            })
            .catch((e) => {
                this.findOneError = e;
                if (this.findOneErrorEvent) {
                    this.findOneErrorEvent.emit(e);
                }
            })
            .finally(() => this.queryByIdLoading = false);
    }
}
